import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { shipmentReturnService } from "../../services/shipmentReturnService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical } from "lucide-react";
import Button from '../common/Button.js';
import { useValidation, ValidationMessage } from "../common/Validation.js";import { useTranslation } from 'react-i18next';


const EditReturn = () => {
  const { t } = useTranslation();
  const { returnId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { errors, validateField } = useValidation()

  const [loading, setLoading] = useState(true)
  const [paymentInitiateLoading, setPaymentInitiateLoading] = useState(false)
  const [paymentRefundLoading, setPaymentRefundLoading] = useState(false)
  const [returnData, setReturnData] = useState(null)

  const [paymentRefundData, setPaymentRefundData] = useState({
    approved: false,
    payment_receipt: null,
    payment_receipt_file: null,
    imagePreview: null,
  })

  useEffect(() => {
    fetchReturn()
  }, [returnId])

  const fetchReturn = async () => {
    try {
      setLoading(true)
      const response = await shipmentReturnService.getReturn(returnId)
      const returnInfo = response.data.return || response.data.data

      if (returnInfo) {
        setReturnData(returnInfo)
        setPaymentRefundData({
          approved: false,
          payment_receipt: null,
          payment_receipt_file: null,
          imagePreview: null,
        })
      } else {
        Alert.error("Return request not found")
      }
    } catch (error) {
      console.error("Error fetching return:", error)
      Alert.error(error.response?.data?.error || "Failed to load return request")
    } finally {
      setLoading(false)
    }
  }

  const canApprove = ["System Admin", "Admin", "Accountant"].includes(user?.role)

  const handleInitiatePayment = async () => {
    const confirmed = await Alert.confirm(
      "Are you sure you want to initiate the payment for this return?",
      "Initiate Payment",
    )

    if (!confirmed) return

    try {
      setPaymentInitiateLoading(true)
      await shipmentReturnService.initiatePayment(returnId)
      Alert.success("Payment initiated successfully!")
      await fetchReturn()
    } catch (error) {
      console.error("Error initiating payment:", error)
      Alert.error(error.response?.data?.error || "Failed to initiate payment")
    } finally {
      setPaymentInitiateLoading(false)
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "📖"
      case "close":
        return "✅"
      case "pending":
        return "⏳"
      case "approved":
        return "✅"
      case "rejected":
        return "❌"
      default:
        return "📋"
    }
  }

  const handleMarkPaymentRefunded = async () => {
    if (!paymentRefundData.approved) {
      validateField('approved', false, () => 'Please check the payment refund approval checkbox')
      Alert.error("Please check the payment refund approval checkbox")
      return
    }
    validateField('approved', true, () => '')

    const confirmed = await Alert.confirm(
      'Are you sure you want to mark the payment as refunded? The shipment status will be updated to "Returned".',
      "Confirm Payment Refund",
    )

    if (!confirmed) return

    try {
      setPaymentRefundLoading(true)
      const formData = new FormData()

      if (paymentRefundData.payment_receipt_file) {
        formData.append("payment_receipt", paymentRefundData.payment_receipt_file)
      }

      await shipmentReturnService.markPaymentRefunded(returnId, formData)
      Alert.success("Payment marked as refunded successfully!")
      navigate("/returns")
    } catch (error) {
      console.error("Error marking payment refunded:", error)
      Alert.error(error.response?.data?.error || "Failed to mark payment refunded")
    } finally {
      setPaymentRefundLoading(false)
    }
  }

  const handleReceiptFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5242880) {
        Alert.error("File size must be less than 5MB")
        return
      }

      const imagePreview = null
      if (file.type.startsWith("image/")) {
        const reader = new FileReader()
        reader.onload = (event) => {
          setPaymentRefundData((prev) => ({
            ...prev,
            imagePreview: event.target.result,
          }))
        }
        reader.readAsDataURL(file)
      }

      setPaymentRefundData({
        ...paymentRefundData,
        payment_receipt_file: file,
        payment_receipt: file.name,
        imagePreview,
      })
    }
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: "bg-yellow-100 text-yellow-800 border-yellow-200",
      close: "bg-green-100 text-green-800 border-green-200",
    }
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading return request..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!returnData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-md text-center">
              <div className="text-6xl mb-4">❌</div>
              <p className="text-red-600 text-lg font-bold mb-2">Return Request Not Found</p>
              <p className="text-gray-600 mb-4">The return request you are looking for does not exist.</p>
              <Button variant="primary" onClick={() => navigate("/returns")}>
                Back to Returns
              </Button>
            </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!canApprove) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-md text-center">
              <div className="text-6xl mb-4">🔒</div>
              <p className="text-red-600 text-lg font-bold mb-2">Access Denied</p>
              <p className="text-gray-600 mb-2">You do not have permission to manage this return request.</p>
              <p className="text-sm text-gray-500 mb-4">
                Only Admin, System Admin, and Accountant roles can perform this action.
              </p>
              <Button variant="primary" onClick={() => navigate("/returns")}>
                Back to Returns
              </Button>
            </div>
        </div>
      </DashboardLayout>
    )
  }

  const isPaymentInitiated = returnData?.payment_initiated?.approved || false
  const isPaymentRefunded = returnData?.payment_refunded?.approved || false

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4 sm:mb-6">

                  {/* LEFT TITLE */}
                  <div className="flex-none">
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      Manage Return Request
                    </h1>
                  </div>

                  {/* RIGHT SECTION (STATUS + BUTTONS) */}
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-0 ml-auto">

                    {/* STATUS BANNER */}
                    <div className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">
                            {returnData.return_status?.toLowerCase() === "open" && "📖"}
                            {returnData.return_status?.toLowerCase() === "close" && "✅"}
                          </span>

                          <div className="flex items-center gap-2">
                            <span className="text-sm">{getStatusIcon(returnData.return_status)}</span>
                            <div>
                              <p className="font-semibold text-sm uppercase text-secondary">
                                {returnData.return_status?.toUpperCase()}
                              </p>
                              <p className="text-xs">
                                {returnData.return_status?.toLowerCase() === "open" &&
                                  "Processing payment workflow"}
                                {returnData.return_status?.toLowerCase() === "close" &&
                                  "Return request completed"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="flex items-center gap-3">
                      <Button variant="primary" onClick={() => navigate(`/shipments/view/${returnData.shipment_id}`)}>
                        View Shipment
                      </Button>

                      <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/returns")}>
                        Back to Returns
                      </Button>
                    </div>

                  </div>

                </div>


                {/* Return Details Card */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-6">
                  <div className="bg-primary px-4 sm:px-6 py-4">
                    <h3 className="text-lg font-semibold text-secondary flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Return Details
                    </h3>
                  </div>

                  <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Return ID</p>
                        <p className="font-bold text-gray-900 font-mono">#{returnData.return_id}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Shipment ID</p>
                        <Button variant="link" onClick={() => navigate(`/shipments/view/${returnData.shipment_id}`)} className="font-bold text-orange-600 font-mono hover:underline">
                          #{returnData.shipment_id}
                        </Button>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('common.createdBy')}</p>
                        <p className="text-gray-900 font-semibold">{returnData.created_by || "N/A"}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Created Date</p>
                        <p className="text-gray-900">
                          {returnData.created_at
                            ? new Date(returnData.created_at).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Processing Ladder */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-6">
                  <div className="bg-primary px-4 sm:px-6 py-4">
                    <h3 className="text-lg font-semibold text-secondary flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Payment Processing Ladder
                    </h3>
                  </div>

                  <div className="p-4 sm:p-6">
                    {/* Step 1: Payment Initiated */}
                    <div className="mb-8">
                      <div className="flex items-start gap-4">
                        <div
                          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg ${
                            isPaymentInitiated ? "bg-green-500" : "bg-blue-500"
                          }`}
                        >
                          1
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <h4 className="text-base font-bold text-gray-900">Step 1: Initiate Payment</h4>
                            {isPaymentInitiated && (
                              <span className="text-green-600 text-xs font-bold px-2 py-1 bg-green-100 rounded">
                                ✓ COMPLETED
                              </span>
                            )}
                          </div>

                          {isPaymentInitiated ? (
                            <div className="bg-green-50 border border-green-200 p-4 rounded-xl mb-4">
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Initiated By:</span>
                                  <span className="font-semibold text-gray-900">
                                    {returnData.payment_initiated?.initiated_by?.user_name || "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Timestamp:</span>
                                  <span className="font-semibold text-gray-900">
                                    {returnData.payment_initiated?.timestamp
                                      ? new Date(returnData.payment_initiated.timestamp).toLocaleString("en-IN")
                                      : "N/A"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant="primary"
                              onClick={handleInitiatePayment}
                              loading={paymentInitiateLoading}
                              className="bg-blue-500 hover:bg-blue-600 border-blue-500 hover:border-blue-600"
                            >
                              {paymentInitiateLoading ? "Processing..." : "Initiate Payment"}
                            </Button>
                          )}
                        </div>
                      </div>
                      {!isPaymentRefunded && <div className="ml-6 h-8 border-l-2 border-gray-300 mt-4"></div>}
                    </div>

                    {/* Step 2: Payment Refunded */}
                    <div>
                      <div className="flex items-start gap-4">
                        <div
                          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg ${
                            isPaymentRefunded ? "bg-green-500" : isPaymentInitiated ? "bg-orange-500" : "bg-gray-300"
                          }`}
                        >
                          2
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <h4 className="text-base font-bold text-gray-900">Step 2: Approve & Refund Payment</h4>
                            {isPaymentRefunded && (
                              <span className="text-green-600 text-xs font-bold px-2 py-1 bg-green-100 rounded">
                                ✓ COMPLETED
                              </span>
                            )}
                          </div>

                          {isPaymentInitiated ? (
                            isPaymentRefunded ? (
                              <div className="bg-green-50 border border-green-200 p-4 rounded-xl">
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Refunded By:</span>
                                    <span className="font-semibold text-gray-900">
                                      {returnData.payment_refunded?.initiated_by?.user_name || "N/A"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Timestamp:</span>
                                    <span className="font-semibold text-gray-900">
                                      {returnData.payment_refunded?.timestamp
                                        ? new Date(returnData.payment_refunded.timestamp).toLocaleString("en-IN")
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                <div>
                                  <label className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-orange-50 transition ${errors.approved ? 'border-red-400' : 'border-orange-200'}`}>
                                    <input
                                      type="checkbox"
                                      checked={paymentRefundData.approved}
                                      onChange={(e) => {
                                        setPaymentRefundData({
                                          ...paymentRefundData,
                                          approved: e.target.checked,
                                        })
                                        validateField('approved', e.target.checked, (v) => !v ? 'Please check the approval checkbox' : '')
                                      }}
                                      disabled={paymentRefundLoading}
                                      className="w-5 h-5 text-orange-500 rounded cursor-pointer"
                                    />
                                    <div>
                                      <p className="font-semibold text-gray-900">I approve this refund</p>
                                      <p className="text-xs text-gray-500">Mark this payment as successfully refunded</p>
                                    </div>
                                  </label>
                                  <ValidationMessage error={errors.approved} />
                                </div>

                                <div className="border-t border-orange-200 pt-4">
                                  <label className="block text-sm font-bold text-gray-900 mb-2">
                                    Payment Receipt (Optional)
                                  </label>
                                  <div className="relative">
                                    <input
                                      type="file"
                                      id="payment_receipt"
                                      onChange={handleReceiptFileChange}
                                      disabled={paymentRefundLoading}
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.gif,.webp"
                                    />
                                    <label
                                      htmlFor="payment_receipt"
                                      className="flex items-center justify-center px-4 py-3 border border-dashed border-orange-300 rounded-xl cursor-pointer hover:border-orange-500 transition-colors"
                                    >
                                      <div className="text-center">
                                        {paymentRefundData.payment_receipt ? (
                                          <>
                                            <svg
                                              className="w-6 h-6 text-green-600 mx-auto mb-1"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                            <p className="text-sm font-semibold text-gray-900">
                                              {paymentRefundData.payment_receipt}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                                          </>
                                        ) : (
                                          <>
                                            <svg
                                              className="w-6 h-6 text-gray-400 mx-auto mb-1"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 4v16m8-8H4"
                                              />
                                            </svg>
                                            <p className="text-sm font-semibold text-gray-700">Upload Payment Receipt</p>
                                            <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG or DOC (max 5MB)</p>
                                          </>
                                        )}
                                      </div>
                                    </label>
                                  </div>

                                  {paymentRefundData.imagePreview && (
                                    <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                                      <p className="text-xs font-semibold text-gray-500 mb-2">Preview:</p>
                                      <img
                                        src={paymentRefundData.imagePreview || "/placeholder.svg"}
                                        alt="Receipt preview"
                                        className="max-w-xs h-auto rounded-xl border border-gray-300 shadow-sm"
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* Refund Button */}
                                <Button
                                  variant="success"
                                  onClick={handleMarkPaymentRefunded}
                                  disabled={!paymentRefundData.approved}
                                  loading={paymentRefundLoading}
                                  fullWidth
                                  size="lg"
                                >
                                  {paymentRefundLoading ? "Processing..." : "Mark Payment as Refunded"}
                                </Button>
                              </div>
                            )
                          ) : (
                            <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl text-center">
                              <p className="text-gray-600 text-sm">
                                <span className="text-lg">⏳</span> Waiting for payment initiation
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
    </DashboardLayout>
  )
}

export default EditReturn;